import { useCallback } from 'react';
import { useSelector } from 'react-redux';
import { TT911 } from '@rsos/capstone/src/constants/capabilities';
import {
  SOURCE_ADMIN_SMS,
  SOURCE_TCAPI,
} from '@rsos/constants/locationSources';
import useHasCapability from '@rsos/hooks/useHasCapability';
import { type EmergencyCallsState } from '../emergencyCalls/emergencyCallsSlice';
import { conversationStateValues } from './conversations.typings';
import {
  useGetTCAPIConversationMetadataQuery,
  useLazyGetTCAPIConversationMetadataQuery,
  useCreateTCAPIConversationMutation,
  useEndTCAPIConversationMutation,
  tcapiAPI,
} from './tcapiAPI';

/**
 * ConversationId prefixes for sources managed by the conversations slice
 */
const CONVERSATION_CHAT_PREFIXES = new Set(['tcapi.', 'admin_sms.']);

/**
 * Check if a conversationId matches a conversation chat prefix
 */
export const isConversationChatPrefix = (conversationId?: string): boolean => {
  if (!conversationId) return false;
  return Array.from(CONVERSATION_CHAT_PREFIXES).some(prefix =>
    conversationId.startsWith(prefix),
  );
};

interface StaticState {
  conversations: Record<string, conversationStateValues>;
  emergencyCalls: EmergencyCallsState;
}

export const getConversationID = (conversation?: conversationStateValues) => {
  if (!conversation || !conversation?.data) {
    return null;
  }

  switch (conversation.source) {
    case SOURCE_TCAPI:
    case SOURCE_ADMIN_SMS:
      return conversation.data.conversation_id;
    default:
      return null;
  }
};

export const getCallerIDFromConversation = (
  conversation?: conversationStateValues,
) => {
  if (!conversation || !conversation?.data) {
    return null;
  }

  switch (conversation.source) {
    case SOURCE_TCAPI:
    case SOURCE_ADMIN_SMS:
      return conversation.data.caller_id;
    default:
      return null;
  }
};

export const getIsConversationEnded = (
  conversation?: conversationStateValues,
) => {
  if (!conversation || !conversation?.data) {
    return false;
  }

  switch (conversation.source) {
    case SOURCE_TCAPI:
      return (
        conversation.data.status === 'inactive' ||
        conversation.data.accept_status === 'timed_out'
      );
    case SOURCE_ADMIN_SMS:
    default:
      return false;
  }
};

export const getHasConversationBeenInitiated = (
  conversation?: conversationStateValues,
) => {
  if (!conversation || !conversation?.data) {
    return false;
  }

  switch (conversation.source) {
    case SOURCE_TCAPI:
      return conversation.data.accept_status === 'accepted';
    case SOURCE_ADMIN_SMS:
      return true; // Conversation has no initiation or ending
    default:
      return false;
  }
};

export const getIsConversationPending = (
  conversation?: conversationStateValues,
) => {
  if (!conversation || !conversation?.data) {
    return false;
  }

  switch (conversation.source) {
    case SOURCE_TCAPI:
      return (
        conversation.data.accept_status === 'pending' &&
        conversation.data.status === 'pending'
      );
    case SOURCE_ADMIN_SMS:
      return false; // Conversation has no initiation or ending
    default:
      return false;
  }
};

/**
 * get most recent value about the conversation for displaying
 */
export const getMostRecentConversationAction = (
  conversation?: conversationStateValues,
): number => {
  if (!conversation || !conversation?.data) {
    return 0;
  }

  switch (conversation.source) {
    case SOURCE_TCAPI:
      return conversation.data.last_updated_time;
    case SOURCE_ADMIN_SMS:
      return conversation.data.conversation_start_time;
    default:
      return 0;
  }
};

export const getHasConversationButtons = (
  conversation?: conversationStateValues,
) => {
  if (!conversation || !conversation?.data) {
    return false; // Hide buttons when conversation doesn't exist or has no data
  }

  switch (conversation.source) {
    case SOURCE_TCAPI:
      return (
        conversation.data.status !== 'inactive' &&
        conversation.data.accept_status !== 'timed_out'
      );
    case SOURCE_ADMIN_SMS:
      return true; // admin_sms always shows buttons
    default:
      return true;
  }
};

/**
 * Get the correct conversationID based on the passed in query
 */
export const useGetConversationID = (query?: string | null) => {
  const conversation = useSelector(
    (state: StaticState) => state.conversations[query || ''],
  );

  return getConversationID(conversation);
};

/**
 * Get the value of isUnread
 */
export const useHasUnreadConversationMessages = (query?: string | null) => {
  const conversation = useSelector(
    (state: StaticState) => state.conversations[query || ''],
  );

  return conversation.isUnread;
};

/**
 * Returns a boolean on whether the conversation has ended or not based on the correct source.
 * Defaults to false, as a conversation can't end if it never started.
 *
 * See useHasConversationBeenInitiated to determine if a conversation has been started or not.
 */
export const useIsConversationEnded = (query?: string | null) => {
  const conversation = useSelector(
    (state: StaticState) => state.conversations[query || ''],
  );

  return getIsConversationEnded(conversation);
};

/**
 * Some sources can't end a conversation. If one of those sources for the callerID matches, returns false
 */
export const useIsConversationAbleToEnd = (query?: string | null) => {
  const conversation = useSelector(
    (state: StaticState) => state.conversations[query || ''],
  );

  const unableToEndSources = new Set([SOURCE_ADMIN_SMS]);

  if (unableToEndSources.has(conversation?.source)) {
    return false;
  }

  return true;
};

/**
 * Grabs the source of the conversation
 */
export const useGetConversationSource = (query?: string | null) => {
  const conversation = useSelector(
    (state: StaticState) => state.conversations[query || ''],
  );

  return conversation?.source;
};

/**
 * Returns a boolean if the conversation has been initiated on user side.
 *
 * See useIsConversationEnded to see if conversation ended or not.
 */
export const useHasConversationBeenInitiated = (query?: string | null) => {
  const conversation = useSelector(
    (state: StaticState) => state.conversations[query || ''],
  );

  return getHasConversationBeenInitiated(conversation);
};

/**
 * Gets the conversation state for a given callerID with proper type safety.
 * Returns the full conversation object including data, source, loading, error, and isUnread state.
 * Returns undefined if no conversation exists for the callerID.
 */
export const useGetConversation = (
  callerID?: string | null,
): conversationStateValues | undefined => {
  const conversation = useSelector((state: StaticState) => {
    if (!callerID) return undefined;
    return state.conversations[callerID];
  });

  return conversation;
};

/**
 * Returns whether Start/End Chat buttons should be visible for this conversation.
 * Hides buttons for ended TCAPI conversations, shows them for all other cases.
 */
export const useHasConversationButtons = (query?: string | null) => {
  const conversation = useGetConversation(query);
  return getHasConversationButtons(conversation);
};

/**
 * Uses the conversationID to grab the key value of the conversation holding it
 */
export const getCallFromConversationID = (
  conversationID?: string | null,
  conversations?: Record<string, conversationStateValues>,
) => {
  if (!conversationID || !conversations) {
    return null;
  }

  for (const key in conversations) {
    if (getConversationID(conversations[key]) === conversationID) {
      return key;
    }
  }
  return null;
};

export const useSelectedConversation = () => {
  const selectedCallerID = useSelector(
    (state: StaticState) => state.emergencyCalls.selectedCallerID,
  );
  const conversation = useSelector(
    (state: StaticState) => state.conversations[selectedCallerID || ''],
  );

  return conversation;
};

/**
 * Grabs the conversation data in relation to the type of call,
 * Since relying on websockets, this call should only be made once onload
 */
export const useGetConversationMetaData = (
  callerID: string | null,
  orgName: string | null | undefined,
  isSMS: boolean,
) => {
  const cachedTCAPIResult = useSelector(
    tcapiAPI.endpoints.getTCAPIConversationMetadata.select({
      callerID,
      orgName,
    }),
  );

  const hasTT911Capability = useHasCapability(TT911).hasCapability;
  const hasCachedTCAPIData = cachedTCAPIResult?.data !== undefined;

  useGetTCAPIConversationMetadataQuery(
    { orgName: orgName || '', callerID: callerID },
    {
      skip:
        !callerID ||
        !orgName ||
        !isSMS ||
        !hasTT911Capability ||
        hasCachedTCAPIData,
    },
  );
};

/**
 * Grabs the rtkquery lazy query of the proper api endpoint to create a conversation
 */
export const useGetCreateConversation = (query?: string | null) => {
  const conversation = useSelector(
    (state: StaticState) => state.conversations[query || ''],
  );

  const tcapiCreate = useCreateTCAPIConversationMutation();

  if (conversation?.source === SOURCE_TCAPI) {
    return tcapiCreate;
  }

  return [];
};

/**
 * Returns a lazy function to fetch conversation metadata for conversation slice sources only.
 * Only handles TCAPI and admin_sms - does not handle TF911.
 * Similar to useGetEndConversation pattern.
 */
export const useLazyGetConversationsMetaData = () => {
  const [triggerTCAPI] = useLazyGetTCAPIConversationMetadataQuery();

  return useCallback(
    (args: { callerID: string; orgName: string | undefined }) => {
      return triggerTCAPI({
        callerID: args.callerID,
        orgName: args.orgName || '',
      });
    },
    [triggerTCAPI],
  );
};

/**
 * Grabs the rtkquery lazy query of the proper api endpoint to end a conversation
 */
export const useGetEndConversation = (query?: string | null) => {
  const conversation = useSelector(
    (state: StaticState) => state.conversations[query || ''],
  );

  const tcapiEnd = useEndTCAPIConversationMutation();

  if (conversation?.source === SOURCE_TCAPI) {
    return tcapiEnd;
  }

  return [];
};

export const useGetConversationIsLoading = (query?: string | null) => {
  const conversation = useSelector(
    (state: StaticState) => state.conversations[query || ''],
  );

  if (!conversation) {
    return false;
  }

  return (
    conversation.loading.createConversation ||
    conversation.loading.endConversation ||
    conversation.loading.getConversationMetadata
  );
};
